import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { briefSchema, type BriefValues } from '@/forms/schemas';
import { ApiError, submissionsApi } from '@/api';
import { errorMessage } from '@/utils/errors';
import { logger } from '@/utils/logger';
import { toast } from 'sonner';
import { trackEvent } from '../../../../utils/analytics';
import {
  initializeFormProtection,
  validateFormProtection,
  createProtectionPayload,
} from '../../../../utils/formProtection';
import { useBotDetection } from '../../../../hooks/useBotDetection';
import { useFormChallenge } from '@/hooks/useFormChallenge';
import { briefForm } from '../../../../content/waq/book';

/** Digits in the emailed code. The server's CODE_LENGTH; a test pins the two. */
export const CODE_LENGTH = 6;

/** How long a code works, for the copy. The server's CODE_TTL_MS. */
export const CODE_MINUTES = 15;

/** Seconds before another code can be asked for, so an impatient double tap is not two mails. */
const RESEND_COOLDOWN_S = 30;

/** What the server sends back when a code is refused, and which of those need a fresh code. */
const NEEDS_NEW_CODE = new Set(['expired', 'attempts', 'changed', 'malformed']);

const fill = (template: string, values: Record<string, string | number>) =>
  template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));

/** The brief as it was screened, and the token its code was issued with. */
type Pending = { payload: Record<string, unknown>; token: string; email: string; sentAt: number };

/*
 * `min-w-0` is load-bearing. A <select> takes its intrinsic width from its
 * widest option -- here "Test Architecture & QA Platform Engineering", 367px --
 * and a grid item's default `min-width: auto` refuses to shrink below that. On
 * a 390px phone the whole column grew past the form and the page scrolled
 * sideways. It applies to every field, not just the select, so a long
 * placeholder can never do the same thing again.
 */
const FIELD =
  'min-w-0 rounded-btn border-[1.5px] border-hair bg-transparent px-[15px] py-[13px] text-[length:var(--text-field-text)] font-medium text-navy placeholder:text-ink-muted';

const LABEL = 'grid min-w-0 gap-[7px] text-field-label font-extrabold uppercase text-soft';

// `normal-case` is load-bearing: these sit inside the <label>, which is
// uppercase, and a shouted "YOUR NAME IS REQUIRED" reads as an alarm.
const ERROR_TEXT = 'text-note font-semibold normal-case text-destructive';

const invalid = (hasError: boolean) =>
  hasError ? `${FIELD} border-destructive focus:border-destructive` : FIELD;

/**
 * The brief beside the scheduler: for someone who would rather describe the
 * problem than pick a slot today.
 *
 * It used to be markup only -- inert `type="button"`, no handler, no endpoint --
 * so everything typed into it was discarded on submit and nobody was told. It
 * now posts to the same `/submit/contact` endpoint as the contact form, through
 * the same validation, the same anti-automation checks and the same error
 * handling, so a brief is a real enquiry that reaches the admin panel.
 *
 * Every enquiry contains the visitor's own description of their needs.
 *
 * Two steps since 2026-10-11, because bots were filling it in. "Send the brief"
 * asks the server to screen the brief and mail a six-digit code to the work
 * email; the brief is only stored and sent to the team once that code is
 * entered. The form stays mounted (hidden) during the code step, so "Edit the
 * brief" returns to exactly what was typed.
 */
export function BriefForm() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<BriefValues>({
    resolver: zodResolver(briefSchema),
    mode: 'onSubmit',
    defaultValues: { name: '', email: '', company: '', message: '' },
  });

  const challenge = useFormChallenge('contact');
  const honeypot = React.useRef<HTMLInputElement>(null);
  const [sent, setSent] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [pending, setPending] = React.useState<Pending | null>(null);
  const [protection, setProtection] = React.useState(() => initializeFormProtection());
  const { validateSubmission } = useBotDetection();

  const onSubmit = async (data: BriefValues) => {
    const botValidation = validateSubmission();
    if (!botValidation.isValid) {
      logger.error('Bot validation failed:', botValidation.error);
      setSubmitError('Security validation failed. Please try again in a moment.');
      return;
    }

    const timed = { ...protection, honeypot: honeypot.current?.value ?? '', submitTime: Date.now() };
    const protectionValidation = validateFormProtection(timed);
    if (!protectionValidation.isValid) {
      logger.error('Form protection validation failed:', protectionValidation.errors);
      setSubmitError('Please take your time filling out the form and try again.');
      return;
    }

    setSubmitError('');

    const payload = {
      name: data.name,
      email: data.email,
      company: data.company,
      role: '',
      subject: 'Booking brief',
      message: data.message,
      protection: createProtectionPayload(timed),
    };

    try {
      const { token } = await submissionsApi.requestContactCode({ ...payload, captchaToken: challenge.getToken() });
      setPending({ payload, token, email: data.email.trim(), sentAt: Date.now() });
    } catch (error) {
      reportFailure(error, 'Brief code request failed:');
    } finally {
      challenge.reset();
    }
  };

  const reportFailure = (error: unknown, label: string) => {
    logger.error(label, error);
    const message = errorMessage(error, 'Please try again in a moment.');

    // A 400 naming automated content is the spam filter, not a broken form.
    if (
      error instanceof ApiError &&
      error.status === 400 &&
      message.includes('invalid or automated content')
    ) {
      setSubmitError(message);
      toast.error('Submission Blocked', { description: message });
      return;
    }

    // Both surfaces, as the contact form did: the alert for someone reading
    // the panel, the toast for someone whose eye is elsewhere on the page.
    setSubmitError(message);
    toast.error(message, { duration: 6000, position: 'top-center' });
  };

  const onVerified = () => {
    // Only after the server has accepted it -- a rejected submission is not a
    // lead. `form_name` is what separates this from the contact form, which
    // reports the same event.
    trackEvent('generate_lead', { form_name: 'brief' });

    setPending(null);
    setSent(true);
    reset();
    // A fresh window, so a second brief is not judged against the first.
    setProtection(initializeFormProtection());
  };

  if (sent) {
    return (
      <div className="flex flex-col justify-center rounded-feature border border-hair bg-surface p-form-p shadow-form">
        <p className="mb-3.5 text-eyebrow font-extrabold uppercase text-blue">Brief received</p>
        <p className="mb-5 text-h3 font-extrabold text-navy">Thanks — we have your brief.</p>
        <p className="mb-7 text-body-lg text-soft">
          An engineer reads it and replies within one business day. If you would rather not wait,
          the calendar beside this form has open slots.
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="cursor-pointer justify-self-start rounded-btn border-[1.5px] border-hair bg-transparent px-[26px] py-[13px] text-btn font-semibold text-navy"
        >
          Send another brief
        </button>
      </div>
    );
  }

  return (
    <>
      {pending && (
        <CodeStep
          pending={pending}
          onResent={(token) => setPending({ ...pending, token, sentAt: Date.now() })}
          onVerified={onVerified}
          onEdit={() => setPending(null)}
        />
      )}
      <form
        noValidate
        hidden={Boolean(pending)}
        onSubmit={handleSubmit(onSubmit)}
        className="relative rounded-feature border border-hair bg-surface p-form-p shadow-form motion-safe:animate-rise"
      >
        <div aria-hidden="true" className="absolute -left-[10000px] top-0 h-px w-px overflow-hidden">
          <label>Leave this field empty<input ref={honeypot} name="brief_extra_detail" tabIndex={-1} autoComplete="off" /></label>
        </div>
        <p className="mb-5 text-faq-q font-extrabold">{briefForm.title}</p>

        {submitError && (
          <p role="alert" className={`mb-4 ${ERROR_TEXT}`}>
            {submitError}
          </p>
        )}

        <div className="grid gap-3.5">
          {briefForm.fields.map((field) => {
            const name = field.name as 'name' | 'email' | 'company';
            const error = errors[name]?.message;

            return (
              <label key={field.name} className={LABEL}>
                {field.label}
                <input
                  type={field.type}
                  placeholder={field.placeholder}
                  autoComplete={field.autoComplete}
                  aria-invalid={error ? true : undefined}
                  className={invalid(Boolean(error))}
                  {...register(name)}
                />
                {error && <span className={ERROR_TEXT}>{error}</span>}
              </label>
            );
          })}

          <label className={LABEL}>
            {briefForm.message.label}
            <textarea
              rows={briefForm.message.rows}
              maxLength={5000}
              placeholder={briefForm.message.placeholder}
              aria-invalid={errors.message ? true : undefined}
              className={`${invalid(Boolean(errors.message))} resize-y`}
              {...register('message')}
            />
            {errors.message && <span className={ERROR_TEXT}>{errors.message.message}</span>}
          </label>

          {challenge.challenge}

          <button
            type="submit"
            disabled={isSubmitting}
            className="cursor-pointer rounded-btn border-none bg-yellow p-4 text-btn font-extrabold text-navy disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? briefForm.verify.sending : briefForm.submit}
          </button>

          <p className="text-note text-soft">{briefForm.note}</p>
        </div>
      </form>
    </>
  );
}

/**
 * The second step: the code from the email.
 *
 * One input rather than six boxes, so paste and the platform's one-time-code
 * autofill both work; it keeps digits only and submits itself once all six are
 * in. A refusal that a retype cannot fix -- expired, too many tries, an edited
 * brief -- says so and points at "Send a new code" rather than at the input.
 */
function CodeStep({
  pending,
  onResent,
  onVerified,
  onEdit,
}: {
  pending: Pending;
  onResent: (token: string) => void;
  onVerified: () => void;
  onEdit: () => void;
}) {
  const challenge = useFormChallenge('contact');
  const copy = briefForm.verify;
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState('');
  const [info, setInfo] = React.useState('');
  const [verifying, setVerifying] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [now, setNow] = React.useState(() => Date.now());
  const inputRef = React.useRef<HTMLInputElement>(null);
  // A ref as well as state: the auto-submit and a click can land in the same tick.
  const busy = React.useRef(false);

  const cooldown = Math.max(0, RESEND_COOLDOWN_S - Math.floor((now - pending.sentAt) / 1000));

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, [pending.token]);

  const verify = async (value: string) => {
    if (busy.current || value.length !== CODE_LENGTH) return;
    busy.current = true;
    setVerifying(true);
    setError('');
    setInfo('');
    try {
      await submissionsApi.submitContact({
        ...pending.payload,
        verification: { token: pending.token, code: value },
      });
      onVerified();
    } catch (err) {
      const reason =
        err instanceof ApiError && err.body && typeof err.body === 'object'
          ? String((err.body as { reason?: unknown }).reason ?? '')
          : '';
      // Already used means the first of two requests got through: the brief is in.
      if (reason === 'used') {
        onVerified();
        return;
      }
      logger.error('Brief code check failed:', err);
      let message = errorMessage(err, 'Please try again in a moment.');
      const left =
        err instanceof ApiError && err.body && typeof err.body === 'object'
          ? (err.body as { attemptsLeft?: unknown }).attemptsLeft
          : undefined;
      if (reason === 'wrong-code' && typeof left === 'number') {
        message = `${message} ${left === 1 ? '1 try left.' : `${left} tries left.`}`;
      }
      setError(message);
      setCode('');
      if (!NEEDS_NEW_CODE.has(reason)) inputRef.current?.focus();
    } finally {
      busy.current = false;
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError('');
    setInfo('');
    try {
      const { token } = await submissionsApi.requestContactCode({ ...pending.payload, captchaToken: challenge.getToken() });
      setCode('');
      setNow(Date.now());
      onResent(token);
      setInfo(copy.resent);
    } catch (err) {
      logger.error('Brief code resend failed:', err);
      setError(errorMessage(err, 'Please try again in a moment.'));
    } finally {
      challenge.reset();
      setResending(false);
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    if (error) setError('');
    if (digits.length === CODE_LENGTH) void verify(digits);
  };

  return (
    <div
      className="rounded-feature border border-hair bg-surface p-form-p shadow-form motion-safe:animate-rise"
      aria-labelledby="brief-code-title"
      role="group"
    >
      <p className="mb-3.5 flex items-center gap-2 text-eyebrow font-extrabold uppercase text-blue">
        <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-blue" />
        {copy.eyebrow}
      </p>
      <p id="brief-code-title" className="mb-3 text-h3 font-extrabold text-navy">
        {copy.title}
      </p>
      <p className="mb-6 text-body text-soft">
        {copy.lead.split('{email}')[0]}
        <strong className="break-words font-bold text-navy">{pending.email}</strong>
        {copy.lead.split('{email}')[1]}
      </p>

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void verify(code);
        }}
        className="grid gap-3.5"
      >
        <label className={LABEL}>
          {copy.label}
          <input
            ref={inputRef}
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={CODE_LENGTH + 4}
            value={code}
            onChange={onChange}
            disabled={verifying}
            placeholder={'•'.repeat(CODE_LENGTH)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'brief-code-error' : undefined}
            className={`${invalid(Boolean(error))} text-center text-[28px]! font-extrabold tabular-nums tracking-[0.45em] placeholder:tracking-[0.45em] disabled:opacity-70`}
          />
        </label>

        {error && (
          <p id="brief-code-error" role="alert" className={ERROR_TEXT}>
            {error}
          </p>
        )}
        {info && !error && (
          <p role="status" className="text-note font-semibold text-blue">
            {info}
          </p>
        )}

        <button
          type="submit"
          disabled={verifying || code.length !== CODE_LENGTH}
          className="cursor-pointer rounded-btn border-none bg-yellow p-4 text-btn font-extrabold text-navy disabled:cursor-not-allowed disabled:opacity-70"
        >
          {verifying ? copy.submitting : copy.submit}
        </button>

        {challenge.challenge}

        <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2 text-meta font-semibold">
          <button
            type="button"
            onClick={resend}
            disabled={cooldown > 0 || resending}
            className="cursor-pointer border-none bg-transparent p-0 text-blue underline underline-offset-4 disabled:cursor-default disabled:text-ink-muted disabled:no-underline"
          >
            {cooldown > 0 ? fill(copy.resendIn, { seconds: cooldown }) : copy.resend}
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="cursor-pointer border-none bg-transparent p-0 text-soft underline underline-offset-4"
          >
            {copy.edit}
          </button>
        </div>

        <p className="text-note text-soft">{fill(copy.note, { minutes: CODE_MINUTES })}</p>
      </form>
    </div>
  );
}
